/**
 * The site's content and the URL space built on it.
 *
 * The posts come from `content/posts/*.md`, loaded by the build through
 * `../vite-content-plugin.ts` and installed into `./content-state.js` — which
 * is what this module reads. NOTHING HERE imports the loader: it reads the
 * filesystem, so reaching it from a page would pull `node:fs`/`node:path` into
 * the browser bundle and break the page.
 *
 * That split is also what makes the specs robust. The build installs the
 * content it read with the ASYNC loader (which includes the AI summaries); a
 * unit test fills the same slot with the plugin's synchronous one, so a spec
 * works whether or not a Vite config was found — and no `virtual:` specifier
 * has to resolve for the module graph to be built.
 *
 * Everything else here is pure: the same helpers drive the pages in the browser
 * and the route list the build prerenders.
 *
 * URL shape — every page is a directory with a trailing slash, so a static
 * host serves `dist/<path>/index.html` at exactly that path:
 *   `/`               the newest posts (page 1)
 *   `/page/2/`        older posts, PAGE_SIZE per page
 *   `/<slug>/`        one post
 *   `/tags/`          every tag with its post count
 *   `/tags/<tag>/`    the posts carrying one tag
 *   `/about/`         the About page
 * Files (no trailing slash): `/feed.xml`, `/atom.xml`, `/sitemap.xml`,
 * `/llms.txt`, `/posts.json`, `/<slug>.md`, `/<slug>/provenance.json`,
 * `/<slug>/summaries.json` and `/<slug>/transcripts/<file>`.
 */

import { getContent, getPosts } from './content-state.js'
import type { Post, PostHeading, PostSection, SiteContent } from './content-types.js'

export type { Post, PostHeading, PostSection, SiteContent }

/** Posts per index page. */
export const PAGE_SIZE = 10

export const TAGS_PATH = '/tags/'
export const ABOUT_PATH = '/about/'
export const FEED_RSS_PATH = '/feed.xml'
export const FEED_ATOM_PATH = '/atom.xml'
export const SITEMAP_PATH = '/sitemap.xml'
export const LLMS_PATH = '/llms.txt'
export const POSTS_JSON_PATH = '/posts.json'

/** The site's content, as the build installed it. */
export function siteContent(): SiteContent {
  return getContent() ?? { posts: [] }
}

/**
 * Every published post, newest first.
 *
 * A function, not a value: `export const posts = ...` would be evaluated while
 * the module graph is built — before the build has installed the content it
 * read — and every page would then be rendered from an empty list.
 */
export function allPosts(): Post[] {
  return getPosts()
}

/** The path of one post. */
export function postPath(slug: string): string {
  return `/${slug}/`
}

/** The path of a post's raw markdown. */
export function postMarkdownPath(slug: string): string {
  return `/${slug}.md`
}

/** The path of a post's provenance map. */
export function postProvenancePath(slug: string): string {
  return `/${slug}/provenance.json`
}

/** The path of a post's published summaries. */
export function postSummariesPath(slug: string): string {
  return `/${slug}/summaries.json`
}

/** The path of one transcript file, published next to its post. */
export function postTranscriptPath(slug: string, file: string): string {
  return `/${slug}/transcripts/${file}`
}

/** The path of an index page; page 1 is the front page. */
export function pagePath(page: number): string {
  return page <= 1 ? '/' : `/page/${page}/`
}

/** The path of one tag's page. */
export function tagPath(tag: string): string {
  return `/tags/${tag}/`
}

/** How many index pages a list of posts needs (at least 1). */
export function pageCount(list: Post[]): number {
  return Math.max(1, Math.ceil(list.length / PAGE_SIZE))
}

/** The posts on one index page (1-based); an empty list past the end. */
export function postsOnPage(list: Post[], page: number): Post[] {
  const start = (page - 1) * PAGE_SIZE
  return list.slice(start, start + PAGE_SIZE)
}

/** One post by slug, or `undefined`. */
export function findPost(slug: string): Post | undefined {
  return allPosts().find((post) => post.slug === slug)
}

/** The posts carrying a tag, newest first. */
export function postsWithTag(tag: string): Post[] {
  return allPosts().filter((post) => post.tags.includes(tag))
}

/** Every tag with its post count, most used first, then alphabetical. */
export function tagCounts(list: Post[] = allPosts()): Array<{ tag: string; count: number }> {
  const counts = new Map<string, number>()
  for (const post of list) for (const tag of post.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1)
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || (a.tag < b.tag ? -1 : 1))
}

/** Every HTML page the site has — what the build prerenders. */
export function sitePaths(list: Post[] = allPosts()): string[] {
  const paths = [pagePath(1)]
  for (let page = 2; page <= pageCount(list); page++) paths.push(pagePath(page))
  for (const post of list) paths.push(postPath(post.slug))
  paths.push(TAGS_PATH)
  for (const { tag } of tagCounts(list)) paths.push(tagPath(tag))
  paths.push(ABOUT_PATH)
  return paths
}
